import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Shield, ArrowLeft, Target, Eye, Heart, Users2 } from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: Target,
      title: 'Customer First',
      description: 'We build features based on real MSP needs and feedback from our community'
    },
    {
      icon: Heart,
      title: 'Simplicity',
      description: 'Powerful features without the complexity. Technology should work for you, not against you'
    },
    {
      icon: Shield,
      title: 'Security',
      description: 'Your data and your clients\' data are protected with enterprise-grade security'
    },
    {
      icon: Users2,
      title: 'Innovation',
      description: 'Continuously improving and adding features to stay ahead of industry needs'
    }
  ];

  const team = [
    { name: 'John Doe', role: 'CEO & Founder', description: 'Former MSP owner with 15+ years experience' },
    { name: 'Jane Smith', role: 'CTO', description: 'Led engineering at Fortune 500 tech companies' },
    { name: 'Mike Johnson', role: 'Head of Product', description: 'Product strategist focused on MSP success' },
    { name: 'Sarah Williams', role: 'VP of Customer Success', description: 'Dedicated to ensuring customer satisfaction' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="mr-2">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">RMM Pro</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Login
              </Button>
              <Button onClick={() => navigate('/signup')} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Built by MSPs, for MSPs
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            We understand the challenges of running a managed service provider because we've been there. 
            Our platform is designed to solve real MSP problems.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Story</h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-6">
            <p>
              RMM Pro was founded in 2020 by a team of former MSP owners who were frustrated with existing 
              tools in the market. They were too complex, too expensive, or simply didn't solve the real 
              problems MSPs face every day.
            </p>
            <p>
              We set out to build something different - a platform that combines the power of enterprise 
              tools with the simplicity and affordability that small and medium-sized MSPs need. After 
              years of development and feedback from hundreds of MSPs, we've created a solution that truly 
              understands your business.
            </p>
            <p>
              Today, RMM Pro serves over 500 MSPs worldwide, managing more than 10,000 endpoints. But we're 
              just getting started. Our mission is to empower every MSP with the tools they need to succeed.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-slate-200 text-center">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Meet Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-6 text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Users2 className="w-12 h-12 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{member.name}</h3>
                  <p className="text-sm text-blue-600 mb-2">{member.role}</p>
                  <p className="text-sm text-slate-600">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">500+</div>
              <div className="text-blue-100">MSP Customers</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">10K+</div>
              <div className="text-blue-100">Managed Endpoints</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">99.9%</div>
              <div className="text-blue-100">Uptime</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">24/7</div>
              <div className="text-blue-100">Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Join Our Growing Community
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            Start your 14-day free trial and see why MSPs choose RMM Pro
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/signup')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg px-8"
          >
            Start Free Trial
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;